// Small inline SVG icons. No icon library — this is the entire icon set the
// call screen needs, so a dependency would cost more than it saves (BRIEF
// section 10: light, no framework the job does not need).

import { useId, type JSX } from "react";

type IconProps = { readonly size?: number };

function base(children: JSX.Element, size = 22): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

// The call controls are filled glyphs on round buttons, the way FaceTime
// draws them (QUEUE row 428): on is a white button with a dark glyph, off a
// dark glass button with a white one. A slash is cut clear of the glyph under
// it, so it still reads at 18px.
const MIC = (
  <>
    <rect x="8.5" y="2.5" width="7" height="12" rx="3.5" fill="currentColor" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
  </>
);

const CAMERA = (
  <>
    <rect x="2" y="6" width="14" height="12" rx="3" fill="currentColor" />
    <path d="M17 10.2l4.2-2.6a.8.8 0 0 1 1.3.7v7.4a.8.8 0 0 1-1.3.7L17 13.8z" fill="currentColor" />
  </>
);

function Slashed({ children }: { readonly children: JSX.Element }): JSX.Element {
  const id = `m${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <g>
      <mask id={id}>
        <rect width="24" height="24" fill="#fff" />
        <path d="M3.5 3.5l17 17" stroke="#000" strokeWidth="5" strokeLinecap="round" />
      </mask>
      <g mask={`url(#${id})`}>{children}</g>
      <path d="M3.5 3.5l17 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </g>
  );
}

export function MicIcon({ size }: IconProps): JSX.Element {
  return base(MIC, size);
}

export function MicOffIcon({ size }: IconProps): JSX.Element {
  return base(<Slashed>{MIC}</Slashed>, size);
}

export function CameraIcon({ size }: IconProps): JSX.Element {
  return base(CAMERA, size);
}

/** A person in front of a soft, dotted background. */
export function BlurIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="9.5" r="3" />
      <path d="M6.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M4 5.5h.01M20 5.5h.01M4 11h.01M20 11h.01" strokeWidth="2.6" />
    </g>,
    size,
  );
}

export function CameraOffIcon({ size }: IconProps): JSX.Element {
  return base(<Slashed>{CAMERA}</Slashed>, size);
}

/** A filled screen with the arrow cut out of it. */
export function ScreenShareIcon({ size }: IconProps): JSX.Element {
  const id = `m${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return base(
    <g>
      <mask id={id}>
        <rect width="24" height="24" fill="#fff" />
        <path d="M9 11l3-3 3 3M12 8v5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </mask>
      <rect x="2.5" y="3.5" width="19" height="13" rx="2.5" fill="currentColor" mask={`url(#${id})`} />
      <path d="M8 20.5h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </g>,
    size,
  );
}

export function PopoutIcon({ size }: IconProps): JSX.Element {
  return base(
    <g>
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="2" fill="none" />
      <rect x="11.5" y="10.5" width="7" height="6" rx="1.2" fill="currentColor" />
    </g>,
    size,
  );
}

export function HangUpIcon({ size }: IconProps): JSX.Element {
  return base(
    <g fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round">
      {/* The handset's ink runs from y 11 to y 17.9, so it is lifted 2.45
          to sit on the circle's center (Adam: "the end icon is a little too
          low in its red circle"). The right half mirrors the left about x 12
          (Adam: "make the hang up button symmetrical like everything else"). */}
      <path
        transform="translate(0 -2.45)"
        d="M4 14C9 10 15 10 20 14L20.4 17.2A1.6 1.6 0 0 1 18.4 17.9L16.4 17.1A1.6 1.6 0 0 1 15.4 15.6V14.6A7 7 0 0 0 8.6 14.6V15.6A1.6 1.6 0 0 1 7.6 17.1L5.6 17.9A1.6 1.6 0 0 1 3.6 17.2Z"
      />
    </g>,
    size,
  );
}

export function PhoneIcon({ size }: IconProps): JSX.Element {
  return base(
    <path
      d="M6.6 3.5h2.6l1.4 4-1.9 1.3a11 11 0 0 0 6.5 6.5l1.3-1.9 4 1.4v2.6a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.7a2 2 0 0 1 2-2.2z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      fill="none"
    />,
    size,
  );
}

export function FlipCameraIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 8a6 6 0 0 0-10-3.5L5 6" />
      <path d="M5 3v3.5h3.5" />
      <path d="M7 16a6 6 0 0 0 10 3.5L19 18" />
      <path d="M19 21v-3.5h-3.5" />
    </g>,
    size,
  );
}

export function HandIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 11V5a1.5 1.5 0 0 1 3 0v5" />
      <path d="M11 10V4a1.5 1.5 0 0 1 3 0v6" />
      <path d="M14 10V5a1.5 1.5 0 0 1 3 0v7" />
      <path d="M17 12v-1a1.5 1.5 0 0 1 3 0v4a5 5 0 0 1-5 5h-2a6 6 0 0 1-5-2.7L5.4 13a1.4 1.4 0 0 1 2-2l1.6 1.6" />
    </g>,
    size,
  );
}

export function SpeakerIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 9v6h3l5 4V5L7 9z" />
      <path d="M16.5 9a4.5 4.5 0 0 1 0 6" />
    </g>,
    size,
  );
}

export function MoreIcon({ size }: IconProps): JSX.Element {
  return base(
    <g fill="currentColor">
      <circle cx="5.5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="18.5" cy="12" r="1.8" />
    </g>,
    size,
  );
}

export function MoreVerticalIcon({ size }: IconProps): JSX.Element {
  return base(
    <g fill="currentColor">
      <circle cx="12" cy="5.5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="18.5" r="1.8" />
    </g>,
    size,
  );
}

export function CheckIcon({ size }: IconProps): JSX.Element {
  return base(
    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
    size,
  );
}

/** The universal record glyph (BRIEF 9/19) — a plain filled dot, no stroke,
 * so it reads distinctly from every other outline icon on the tray. */
export function RecordIcon({ size }: IconProps): JSX.Element {
  return base(<circle cx="12" cy="12" r="7" fill="currentColor" />, size);
}

export function GearIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </g>,
    size,
  );
}

export function HourglassIcon({ size }: IconProps): JSX.Element {
  return base(
    <path d="M7 3h10M7 21h10M8 3v3.5a4 4 0 0 0 1.6 3.2L12 12l2.4-2.3A4 4 0 0 0 16 6.5V3M8 21v-3.5a4 4 0 0 1 1.6-3.2L12 12l2.4 2.3a4 4 0 0 1 1.6 3.2V21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
    size,
  );
}

export function BackIcon({ size }: IconProps): JSX.Element {
  return base(
    <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
    size,
  );
}

export function ShareIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="M7.5 7.5L12 3l4.5 4.5" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </g>,
    size,
  );
}

export function LinkIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
      <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
    </g>,
    size,
  );
}

export function ChatIcon({ size }: IconProps): JSX.Element {
  return base(
    <path
      d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-7l-4.4 3.3a.5.5 0 0 1-.8-.4V17H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z"
      fill="currentColor"
    />,
    size,
  );
}

export function TrashIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 7h15" />
      <path d="M9.5 7V5.2A1.2 1.2 0 0 1 10.7 4h2.6a1.2 1.2 0 0 1 1.2 1.2V7" />
      <path d="M6.5 7l.9 11.6A1.6 1.6 0 0 0 9 20h6a1.6 1.6 0 0 0 1.6-1.4L17.5 7" />
      <path d="M10.2 11v5.5M13.8 11v5.5" />
    </g>,
    size,
  );
}

/** The Breeze mark, from public/breeze-mark.svg: two wind strokes, filled with
 * the current color so it takes the button's tone. */
export function BreezeMarkIcon({ size = 24 }: IconProps): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="230 190 600 650" fill="currentColor" aria-hidden="true">
      <g transform="translate(-28 0)">
        <path d="M270 196 L640 196 C745 196 815 258 815 342 C815 442 735 516 625 522 C530 527 460 504 390 498 C340 494 300 498 262 512 C300 458 352 432 420 432 C490 432 545 452 610 450 C675 448 722 412 726 370 C730 330 702 314 655 314 L400 314 C335 314 290 336 270 384 C272 320 270 250 270 196 Z" />
        <path d="M262 742 C316 676 396 652 470 668 C540 684 600 714 660 714 C715 714 745 682 745 640 C745 600 725 570 700 552 C790 560 846 610 846 684 C846 772 776 834 680 834 C600 834 540 812 470 790 C400 768 330 752 262 742 Z" />
      </g>
    </svg>
  );
}

export function CloseIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </g>,
    size,
  );
}

export function PrepIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h4" />
      <path d="M9 12h6M9 16h6" />
    </g>,
    size,
  );
}

export function ChevronIcon({ size }: IconProps): JSX.Element {
  return base(
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
    size,
  );
}

export function GripIcon({ size }: IconProps): JSX.Element {
  return base(
    <g fill="currentColor">
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </g>,
    size,
  );
}

export function PlusIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </g>,
    size,
  );
}

export function PencilIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20h4L19 9a2.1 2.1 0 0 0-4-4L4 16v4z" />
      <path d="M13.5 6.5l4 4" />
    </g>,
    size,
  );
}

export function SearchIcon({ size }: IconProps): JSX.Element {
  return base(
    <g stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
      <circle cx="10.5" cy="10.5" r="6" fill="none" />
      <path d="M15 15l5 5" />
    </g>,
    size,
  );
}

export function MinusIcon({ size }: IconProps): JSX.Element {
  return base(<path d="M6 12h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />, size);
}
